import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, Send, Mail, X } from "lucide-react";
import { toast } from "sonner";

export default function ShareReportModal({
  open,
  onClose,
  snapshot,
  shareUrl,
  onSendSlack,
  slackConnected
}) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);

  const text = useMemo(() => {
    if (!snapshot) return "";
    const {
      windowDays,
      score,
      deltaText,
      topDriver,
      topDriverRate,
      topDriverImpact,
      benchmarkText,
      monthlyLoss,
      potentialSavings,
      expectedReduction,
      playbooks
    } = snapshot;

    const pb = playbooks?.slice(0, 3) || [];
    const pbLines = pb.length > 0 
      ? pb.map((p) => `- ${p}`).join("\n")
      : "- Review Top Drivers\n- Apply Playbooks\n- Track improvement weekly";

    return `Commerce Index — Promise Health Snapshot (Last ${windowDays || 7} days)

Promise Integrity Score: ${score ?? "—"}/100 (${deltaText || "—"})
Top driver: ${topDriver || "—"} (${topDriverRate ?? "—"}% of promises, weighted impact ${topDriverImpact ?? "—"})
Benchmark: ${benchmarkText || "Benchmark not available yet"}

Estimated monthly leakage: $${monthlyLoss ?? "—"}
Potential savings (if breaks reduced by ${expectedReduction ?? "—"}%): $${potentialSavings ?? "—"}

Next actions:
${pbLines}

Open dashboard: ${shareUrl || ""}`;
  }, [snapshot, shareUrl]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Failed to copy");
    }
  }

  async function handleSendSlack() {
    if (!slackConnected || !onSendSlack) return;
    setSending(true);
    try {
      await onSendSlack(text);
      toast.success("Sent to Slack");
    } catch (e) {
      toast.error("Failed to send to Slack");
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl bg-[#0B1220] border-white/10" data-testid="share-report-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-semibold">
            Share Promise Health Report
          </DialogTitle>
          <DialogDescription className="text-slate-300/80 text-sm">
            Send a quick snapshot to your team — score, top driver, benchmark position, and estimated savings.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {/* Snapshot Summary */}
          {snapshot && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-xs text-slate-400">Score</div>
                <div className="text-xl font-semibold text-white">{snapshot.score ?? "—"}/100</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-xs text-slate-400">Top Driver</div>
                <div className="text-sm font-medium text-white truncate">{snapshot.topDriver || "—"}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-xs text-slate-400">Monthly Loss</div>
                <div className="text-sm font-medium text-rose-400">${snapshot.monthlyLoss ?? "—"}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-xs text-slate-400">Benchmark</div>
                <div className="text-xs font-medium text-white truncate">{snapshot.benchmarkText || "Pending"}</div>
              </div>
            </div>
          )}

          {/* Copy/paste summary */}
          <div className="rounded-xl bg-white/5 border border-white/10 p-4">
            <div className="text-xs text-slate-300/70 mb-2">Copy/paste summary</div>
            <textarea
              className="w-full rounded-xl bg-[#0B1220] border border-white/10 px-3 py-2 text-sm text-slate-200 outline-none focus:border-sky-400/40 focus:ring-2 focus:ring-sky-400/15 resize-none"
              rows={10}
              value={text}
              readOnly
              data-testid="share-report-text"
            />

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Button
                variant="outline"
                className="bg-sky-500/15 border-sky-400/30 hover:bg-sky-500/25 hover:border-sky-400/40 text-sky-100"
                onClick={handleCopy}
                data-testid="copy-report-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy to clipboard
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                className={`border transition ${
                  slackConnected
                    ? "bg-emerald-500/15 border-emerald-400/30 hover:bg-emerald-500/25 hover:border-emerald-400/40 text-emerald-100"
                    : "bg-white/5 border-white/10 text-slate-400 opacity-50 cursor-not-allowed"
                }`}
                disabled={!slackConnected || sending}
                onClick={handleSendSlack}
                data-testid="send-slack-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? "Sending..." : slackConnected ? "Send to Slack" : "Connect Slack first"}
              </Button>

              <Button
                variant="outline"
                className="bg-white/5 border-white/10 text-slate-400 opacity-60 cursor-not-allowed"
                disabled
              >
                <Mail className="w-4 h-4 mr-2" />
                Email (coming soon)
              </Button>
            </div>

            <div className="text-xs text-slate-300/60 mt-3">
              Note: Benchmarks may be unavailable if your peer cohort is too small (privacy-safe thresholds).
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
